import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPaperPlane } from 'react-icons/fa';
import { collection, query, where, getDocs, doc, getDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { containsProfanity, containsLink } from '../utils/profanityFilter';
import InteractiveGlobe from './InteractiveGlobe';
import { MessageSourceCard } from './globe/MessageSourceCard';
import { CountryTag } from './globe/CountryTag';
import { Button } from './ds/core/Button';
import './Globe.css';

// Natural Earth world polygons (InteractiveGlobe reads ISO_A2 from here)
const WORLD_GEOJSON_URL = '/world.geojson';

const rgbFromHex = (hex) => {
  const h = (hex || '').replace('#', '');
  const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (n.length !== 6) return null;
  return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
};
const composerStyleFor = (hex) => {
  const rgb = rgbFromHex(hex);
  if (!rgb) return {};
  const [r, g, b] = rgb;
  const light = 0.299 * r + 0.587 * g + 0.114 * b > 150;
  return {
    background: `rgba(${r}, ${g}, ${b}, 0.72)`,
    '--composer-fg': light ? '#1a1f33' : '#fff',
    '--composer-fg-muted': light ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.55)',
  };
};

function Globe({ earthColor = '#2faf68', oceanColor = '#145fb0', bgColor, markerColor, graticuleColor, glowColor, glowOpacity, selectedColor, coastColor, coastWidth, ambient, sunIntensity, flat, skin, setBgColor, setNavbarColor, setUser, setBgImage, setNavbarImage }) {
  const navigate = useNavigate();
  const [messageCountryData, setMessageCountryData] = useState({});
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [hoverCountry, setHoverCountry] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const [noMsgToast, setNoMsgToast] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const hoverCountryRef = useRef(null);

  const [worldMessage, setWorldMessage] = useState('');
  const [sendingWorldMessage, setSendingWorldMessage] = useState(false);
  const [worldMessageStatus, setWorldMessageStatus] = useState('');

  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        // Fetch user's location based on IP using a free geolocation API
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();

        if (data.latitude && data.longitude) {
          setUserLocation({
            latitude: data.latitude,
            longitude: data.longitude,
            country: data.country_name,
            city: data.city
          });
        }
      } catch (error) {
        console.error('Failed to fetch user location:', error);
        // Fallback to a default location if geolocation fails
        setUserLocation({
          latitude: 0,
          longitude: 0,
          country: 'Unknown',
          city: 'Unknown'
        });
      }
    };

    fetchUserLocation();
  }, []);

  useEffect(() => {
    const loadMessagesAndCountries = async () => {
      try {
        const userId = localStorage.getItem('userId');
        if (!userId) return;

        // Load messages from Firebase
        const messagesQuery = query(
          collection(db, 'messages'),
          where('receiverId', '==', userId)
        );
        const messagesSnapshot = await getDocs(messagesQuery);
        const messages = messagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const byCountry = {};
        messages.forEach(msg => {
          const iso = msg.senderCountryCode;
          if (!iso) return;
          if (!byCountry[iso]) byCountry[iso] = { count: 0, lastText: '', senderId: null };
          byCountry[iso].count += 1;
          byCountry[iso].lastText = msg.text || byCountry[iso].lastText;
          byCountry[iso].senderId = msg.senderId || byCountry[iso].senderId;
        });

        setMessageCountryData(byCountry);
      } catch (err) {
        console.error('Failed to load data:', err);
      }
    };
    loadMessagesAndCountries();
  }, []);

  useEffect(() => {
    const track = (e) => {
      if (hoverCountryRef.current) setHoverPos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', track);
    return () => window.removeEventListener('mousemove', track);
  }, []);

  const handleSendWorldMessage = async (e) => {
    e.preventDefault();
    if (!worldMessage.trim() || sendingWorldMessage) return;

    setSendingWorldMessage(true);
    setWorldMessageStatus('');

    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        setWorldMessageStatus('Please log in to send messages');
        setSendingWorldMessage(false);
        return;
      }

      if (containsProfanity(worldMessage)) {
        setWorldMessageStatus('Message contains inappropriate language');
        setSendingWorldMessage(false);
        return;
      }

      if (containsLink(worldMessage)) {
        setWorldMessageStatus('Links are not allowed in messages');
        setSendingWorldMessage(false);
        return;
      }

      const existingConvSnapshot = await getDocs(query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', userId)
      ));
      const usersWithConversations = new Set();
      existingConvSnapshot.docs.forEach(convDoc => {
        convDoc.data().participants.forEach(participantId => {
          if (participantId !== userId) usersWithConversations.add(participantId);
        });
      });

      const usersSnapshot = await getDocs(collection(db, 'users'));
      const safeUsers = usersSnapshot.docs
        .filter(d => {
          const data = d.data();
          return d.id !== userId && !usersWithConversations.has(d.id) && !data.pauseAnonymousMessages;
        })
        .map(d => ({ id: d.id }));

      const senderDoc = await getDoc(doc(db, 'users', userId));
      const senderCountryCode = senderDoc.data()?.countryCode || null;

      if (safeUsers.length === 0) {
        const pendingData = { senderId: userId, text: worldMessage.trim(), createdAt: serverTimestamp(), status: 'pending' };
        if (senderCountryCode) pendingData.senderCountryCode = senderCountryCode;
        await addDoc(collection(db, 'pendingWorldMessages'), pendingData);
        setWorldMessage('');
        setWorldMessageStatus('Message queued! It will be delivered when a new user joins.');
        setTimeout(() => setWorldMessageStatus(''), 5000);
        setSendingWorldMessage(false);
        return;
      }

      const randomUser = safeUsers[Math.floor(Math.random() * safeUsers.length)];

      // Safety guard: never deliver a world message back to the sender
      if (!randomUser || randomUser.id === userId) {
        setWorldMessageStatus('Could not find someone to send to right now. Try again soon!');
        setSendingWorldMessage(false);
        return;
      }

      const conversationRef = await addDoc(collection(db, 'conversations'), {
        participants: [userId, randomUser.id],
        lastMessage: worldMessage.trim(),
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      });

      const messageData = {
        conversationId: conversationRef.id,
        senderId: userId,
        receiverId: randomUser.id,
        text: worldMessage.trim(),
        timestamp: serverTimestamp(),
        read: false
      };
      if (senderCountryCode) messageData.senderCountryCode = senderCountryCode;
      await addDoc(collection(db, 'messages'), messageData);

      setWorldMessage('');
      setWorldMessageStatus('Sent! It will reach a random stranger.');
      setTimeout(() => setWorldMessageStatus(''), 4000);
    } catch (err) {
      console.error('Error sending world message:', err);
      setWorldMessageStatus('Failed to send message');
    } finally {
      setSendingWorldMessage(false);
    }
  };

  return (
    <div className="globe-container">
      <div className="globe-canvas">
        <InteractiveGlobe
          messageCountries={messageCountryData}
          earthColor={earthColor}
          oceanColor={oceanColor}
          geojsonUrl={WORLD_GEOJSON_URL}
          userLocation={userLocation}
          {...(markerColor != null ? { markerColor } : {})}
          {...(graticuleColor != null ? { graticuleColor } : {})}
          {...(glowColor != null ? { glowColor } : {})}
          {...(glowOpacity != null ? { glowOpacity } : {})}
          {...(ambient != null ? { ambient } : {})}
          {...(sunIntensity != null ? { sunIntensity } : {})}
          {...(selectedColor != null ? { selectedColor } : {})}
          {...(coastColor != null ? { coastColor } : {})}
          {...(coastWidth != null ? { coastWidth } : {})}
          flat={!!flat}
          onCountryHover={(c) => { hoverCountryRef.current = c; setHoverCountry(c); }}
          onCountrySelect={(c) => {
            if (c && c.hasMessage) {
              setSelectedCountry(c);
            } else {
              setSelectedCountry(null);
              if (c) {
                setNoMsgToast(`No messages from ${c.name} yet`);
                setTimeout(() => setNoMsgToast(null), 2200);
              }
            }
          }}
        />
      </div>

      {/* Hover tooltip — flag + country name following the cursor */}
      {hoverCountry && (
        <div className="globe-hover-tag" style={{ left: hoverPos.x + 14, top: hoverPos.y + 14 }}>
          <CountryTag iso={hoverCountry.iso} name={hoverCountry.name} tone="glass" size="sm" pin={hoverCountry.hasMessage} />
        </div>
      )}

      {selectedCountry && messageCountryData[selectedCountry.iso] && (
        <div className="message-source-popover">
          <MessageSourceCard
            iso={selectedCountry.iso}
            country={selectedCountry.name}
            count={messageCountryData[selectedCountry.iso].count}
            preview={messageCountryData[selectedCountry.iso].lastText}
            onClose={() => setSelectedCountry(null)}
            onOpen={messageCountryData[selectedCountry.iso].senderId ? () => {
              navigate(`/messages?userId=${messageCountryData[selectedCountry.iso].senderId}`);
              setSelectedCountry(null);
            } : undefined}
          />
        </div>
      )}

      {noMsgToast && <div className="globe-toast">{noMsgToast}</div>}

      {worldMessageStatus && <div className="world-composer-status" style={composerStyleFor(bgColor)}>{worldMessageStatus}</div>}

      <form className="world-composer" onSubmit={handleSendWorldMessage} style={composerStyleFor(bgColor)}>
        <FaPaperPlane className="world-composer-icon" />
        <input
          className="world-composer-input"
          value={worldMessage}
          onChange={(e) => setWorldMessage(e.target.value)}
          placeholder={skin === 'wired' ? 'transmit to the wired_' : skin === 'moe' ? 'つぶやく... whisper a message to the world~' : 'Send a message to the world…'}
          maxLength={500}
          disabled={sendingWorldMessage}
        />
        {skin === 'wired' && <span className="wired-cursor" aria-hidden="true">▋</span>}
        <Button variant="primary" size="md" type="submit" disabled={sendingWorldMessage || !worldMessage.trim()}>
          {sendingWorldMessage ? 'Sending…' : 'Send'}
        </Button>
      </form>

    </div>
  );
}

export default Globe;
